import React from 'react';
import { SerializedStyles, css } from '@emotion/react';
import { useTheme } from '@veneer/theme';

const useStyles = (): {
  root: {
    base: SerializedStyles;
    actionBottom: SerializedStyles;
    caution: SerializedStyles;
    custom: SerializedStyles;
    help: SerializedStyles;
    informative: SerializedStyles;
    positive: SerializedStyles;
    negative: SerializedStyles;
    warning: SerializedStyles;
    pill: SerializedStyles;
    hc: SerializedStyles;
  };
  centerContainer: {
    base: SerializedStyles;
    actionTopPlacement: SerializedStyles;
  };
  contentContainer: {
    base: SerializedStyles;
    singleContent: SerializedStyles;
  };
  textContainer: {
    base: SerializedStyles;
  };
  icon: {
    base: SerializedStyles;
    type: {
      caution: SerializedStyles;
      custom: SerializedStyles;
      help: SerializedStyles;
      informative: SerializedStyles;
      positive: SerializedStyles;
      negative: SerializedStyles;
      warning: SerializedStyles;
    };
  };
  title: SerializedStyles;
  description: SerializedStyles;
  date: SerializedStyles;
  action: {
    base: SerializedStyles;
    ellipsis: SerializedStyles;
    top: SerializedStyles;
  };
  buttonsContainer: {
    base: SerializedStyles;
  };
} => {
  const theme = useTheme();
  const INLINE_MIN_HEIGHT = `calc(${theme.layout.size8} + ${theme.layout.size4})`;

  return React.useMemo(
    () => ({
      root: {
        base: css({
          display: 'grid',
          gridTemplateRows: 'auto',
          gridTemplateColumns: '1fr auto',
          padding: theme.layout.size3,
          paddingRight: theme.layout.size2,
          minHeight: INLINE_MIN_HEIGHT,
          boxSizing: 'border-box',
          borderRadius: theme.borderRadius4,
        }),
        actionBottom: css({
          gap: theme.layout.size2,
        }),
        caution: css({
          backgroundColor: theme.color.caution.background.default,
        }),
        custom: css({
          backgroundColor: theme.color.background.default,
        }),
        help: css({
          backgroundColor: theme.color.help.background.default,
        }),
        informative: css({
          backgroundColor: theme.color.neutral.background.default,
        }),
        positive: css({
          backgroundColor: theme.color.positive.background.default,
        }),
        negative: css({
          backgroundColor: theme.color.negative.background.default,
        }),
        warning: css({
          backgroundColor: theme.color.warning.background.default,
        }),
        pill: css({
          borderRadius: theme.layout.size4,
        }),
        hc: css({
          border: `2px solid ${theme.color.foreground.default}`,
          backgroundColor: 'transparent',
        }),
      },
      centerContainer: {
        base: css({
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
        }),
        actionTopPlacement: css({
          flexDirection: 'row',
          gap: 8,
        }),
      },
      contentContainer: {
        base: css({
          display: 'flex',
          gap: theme.layout.size3,
          alignItems: 'flex-start',
          overflow: 'hidden',
        }),
        singleContent: css({
          alignItems: 'center',
        }),
      },
      textContainer: {
        base: css({
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          width: '100%',
          wordBreak: 'break-word',
          flexDirection: 'column',
        }),
      },
      title: css({
        ...theme.typography.labelRegular,
        alignItems: 'center',
        color: theme.color.foreground.default,
        display: 'flex',
        minHeight: theme.layout.size6,
      }),
      description: css({
        ...theme.typography.captionRegular,
        color: theme.color.foreground.medium,
      }),
      date: css({
        ...theme.typography.labelSmall,
        color: theme.color.foreground.light,
        paddingTop: theme.layout.size1,
      }),
      buttonsContainer: {
        base: css({
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'flex-start',

          ['@media (width <= 280px)']: {
            flexDirection: 'column-reverse',
          },
        }),
      },
      action: {
        base: css({
          paddingTop: theme.layout.size2,
        }),
        ellipsis: css({
          span: {
            overflow: 'hidden',
          },
        }),
        top: css({
          paddingTop: 0,
        }),
      },
      icon: {
        base: css({
          minWidth: theme.layout.size6,
          minHeight: theme.layout.size6,
        }),
        type: {
          caution: css({
            fill: theme.color.gold7,
          }),
          custom: css({
            fill: theme.color.hpBlue7,
          }),
          help: css({
            fill: theme.color.vividSkyBlue6,
          }),
          informative: css({
            fill: theme.color.gray6,
          }),
          positive: css({
            fill: theme.color.green6,
          }),
          negative: css({
            fill: theme.color.red6,
          }),
          warning: css({
            fill: theme.color.darkOrange7,
          }),
        },
      },
    }),
    [theme, INLINE_MIN_HEIGHT]
  );
};

export default useStyles;
